import React, { useState } from 'react';
import api from '../../api';

interface ClearResult {
  success: boolean;
  message: string;
  details?: Array<{
    level: number;
    success: boolean;
    result?: any;
    error?: string;
  }>;
}

interface ClearCacheButtonProps {
  projectId: string | number;
}

const ClearCacheButton: React.FC<ClearCacheButtonProps> = ({ projectId }) => {
  const [isClearing, setIsClearing] = useState<boolean>(false);
  const [clearResult, setClearResult] = useState<ClearResult | null>(null);

  const clearCache = async (): Promise<void> => {
    setIsClearing(true);
    setClearResult(null);

    try {
      // Clear all generation locks for this project
      const levels: number[] = [1, 2, 3, 4];
      const clearPromises = levels.map(async (level: number) => {
   
        try {
          const token = localStorage.getItem('jarvis_access_token');
          const response =  await api.delete(`/api/analysis/debug/locks/${projectId}/${level}`, 
            {
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
            }
          });
          
          //@ts-expect-error
          if (response.ok) {
             //@ts-expect-error
            const result = await response.json();
            return { level, success: true, result };
          } else {
             //@ts-expect-error
            const errorText = await response.text();
            return { level, success: false, error: errorText || 'Failed to clear' };
          }
        } catch (error) {
          return { 
            level, 
            success: false, 
            error: error instanceof Error ? error.message : 'Unknown error'
          };
        }
      });

      const results = await Promise.all(clearPromises);
      
      // Clear local storage cache
      const cacheKeys: string[] = [
        `processMap_${projectId}_1`,
        `processMap_${projectId}_2`,
        `processMap_${projectId}_3`,
        `processMap_${projectId}_4`,
        `generationStatus_${projectId}`,
        `analysisResults_${projectId}`
      ];
      
      cacheKeys.forEach((key: string) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      const successfulClears = results.filter(r => r.success);
      setClearResult({
        success: true,
        message: `Cleared locks for levels: ${successfulClears.map(r => r.level).join(', ')}`,
        details: results
      });

      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      setClearResult({
        success: false,
        message: `Failed to clear cache: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="clear-cache-container">
      <button
        onClick={clearCache}
        disabled={isClearing}
        className="clear-cache-btn"
        title="Clear generation locks and local cache"
        type="button"
      >
        {isClearing ? (
          <>
            <span className="spinner"></span>
            Clearing Cache...
          </>
        ) : (
          <>
            <span className="cache-icon" role="img" aria-label="trash">
              🗑️
            </span>
            Clear Cache
          </>
        )}
      </button>
      
      {clearResult && (
        <div className={`clear-result ${clearResult.success ? 'success' : 'error'}`}>
          <p>{clearResult.message}</p>
          {clearResult.success && (
            <small>Page will refresh automatically...</small>
          )}
        </div>
      )}
    </div>
  );
};

export default ClearCacheButton;